import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap';
import Modal from '../../components/ModalVoltar';
import SlideshowImovel from './SlideshowImovel';
import ModalCopiarImovel from './ModalCopiarImovel';
import { api } from '../../lib/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { CAMPOS_PUBLICAR } from './constantes';
import { formatarMoeda, textoDatasImovel } from './formatos';

// Janela de visualização rápida (o "modal do imóvel" da lista do legado):
// fotos, valores, endereço, resumo, descrição, proprietário/descrição
// interna, compartilhamento e as ações Alterar/Excluir.
export default function DetalheImovel({ publicId, aoFechar, aoExcluido }) {
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const [copiando, setCopiando] = useState(false);
  const toast = useToast();
  const confirmar = useConfirm();
  const [imovel, setImovel] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  useEffect(() => {
    if (!publicId) return undefined;
    let cancelado = false;
    setImovel(null);
    api
      .get(`/imoveis/${publicId}`)
      .then((r) => !cancelado && setImovel(r.data))
      .catch((err) => {
        if (!cancelado) {
          toast.erro(err.message || 'Não foi possível carregar o imóvel');
          aoFechar();
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  async function excluir() {
    if (!(await confirmar({ titulo: 'Excluir imóvel', mensagem: 'Deseja deletar este registro? As fotos também serão apagadas.', variante: 'perigo', textoConfirmar: 'Excluir' }))) return;
    setExcluindo(true);
    try {
      await api.delete(`/imoveis/${publicId}`);
      toast.sucesso('Imóvel excluído.');
      aoExcluido();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível excluir o imóvel');
    } finally {
      setExcluindo(false);
    }
  }

  async function copiar(texto) {
    try {
      await navigator.clipboard.writeText(texto);
      toast.sucesso('Link copiado.');
    } catch {
      toast.erro('Não foi possível copiar o link.');
    }
  }

  const im = imovel;
  const rascunho = im?.i1_rascunho === 'true';
  const ativo = im && !rascunho && im.i1_situacao === 'Ativo';

  // textos de compartilhamento (mesmos do painel legado)
  const linkPortal = im?.links?.portal;
  const textoWhats = im
    ? `Gostaria de compartilhar esse imóvel com você, referência ${im.i1_codsequencia}\n\n*${im.i1_seotitulo || ''}*\n${linkPortal || ''}\n\nEm ${im.i1_cidade || ''}, ${im.i1_estado || ''}\n\n`
    : '';
  const linkFacebook = im && linkPortal
    ? `https://www.facebook.com/dialog/share?app_id=487768485268687&display=popup&href=${encodeURIComponent(linkPortal)}&redirect_uri=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(`${im.i1_seotitulo || ''}\n\nReferência: ${im.i1_codsequencia}\n\nLink: ${linkPortal}\n\nAcesse o LINK para mais informações`)}`
    : null;
  const celularProp = im?.proprietario?.celular ? String(im.proprietario.celular).replace(/\D/g, '') : '';

  const faltantes = im
    ? CAMPOS_PUBLICAR.filter(([campo]) => (campo === 'proprietario' ? !im.proprietario : !String(im[campo] ?? '').trim() || String(im[campo]).trim() === '0'))
    : [];

  // Cor da situação (mesmas cores do painel antigo: faixa verde = ativo, vermelha = inativo,
  // cinza = cadastro incompleto) — vale pro cabeçalho inteiro da janela.
  const cor = !im ? '#6c757d' : rascunho ? '#8a8a8a' : ativo ? '#28a745' : '#dc3545';
  const situacaoTexto = !im ? '' : rascunho ? 'Cadastro incompleto' : ativo ? 'Ativo' : im.i1_situacao || 'Inativo';

  return (
    <Modal show={!!publicId} onHide={aoFechar} size="lg" centered scrollable>
      <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: cor, color: '#fff', borderBottom: 0 }}>
        <Modal.Title className="h6 mb-0">
          {im ? (
            <>
              {/* "CH00107 - #692": referência e nº do imóvel (o "ID Internet" do painel antigo) */}
              <div className="fw-bold" style={{ fontSize: '1.1rem' }}>
                {im.i1_codsequencia} - #{im.id_internet}
              </div>
              <div className="fw-normal small">
                {situacaoTexto}
                {im.i1_seotitulo ? ` · ${im.i1_seotitulo}` : ''}
              </div>
            </>
          ) : (
            'Carregando...'
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: !im ? undefined : rascunho ? '#f1f1f1' : ativo ? '#f3faf5' : '#fdf3f3' }}>
        {!im && <Spinner animation="border" />}
        {im && (
          <>
            {im.imagens.length > 0 ? (
              <div className="mb-3">
                <SlideshowImovel fotos={im.imagens} />
              </div>
            ) : (
              <Alert variant="light" className="border text-center">Sem fotos</Alert>
            )}

            {rascunho && (
              <Alert variant="danger">
                <strong>O cadastro não está completo.</strong> Para que o imóvel fique disponível, revise:
                <ul className="mb-0">
                  {faltantes.map(([campo, rotulo]) => (
                    <li key={campo}>{rotulo}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {!rascunho && im.i1_situacao !== 'Ativo' && <Alert variant="danger" className="text-center">Cadastro inativo</Alert>}

            <Row className="g-3">
              <Col md={6}>
                <div className="border rounded p-3 mb-3">
                  <div className="text-muted small">Venda</div>
                  <div className="h5 text-primary text-end">{formatarMoeda(im.i1_vlrvenda)}</div>
                  {Number(im.i1_locacao) > 0 && (
                    <>
                      <div className="text-muted small">Locação</div>
                      <div className="h6 text-primary text-end">{formatarMoeda(im.i1_locacao)}</div>
                    </>
                  )}
                  <hr />
                  <div>{[im.i1_endereco, im.i1_numero].filter(Boolean).join(', ')}</div>
                  <div>{im.i1_bairroofi}</div>
                  <div>{[im.i1_cidade, im.i1_estado].filter(Boolean).join(', ')}</div>
                  <a
                    className="small"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://www.google.com/maps?f=d&daddr=${encodeURIComponent([im.i1_endereco, im.i1_numero, im.i1_cidade, im.i1_estado, im.i1_cep].filter(Boolean).join(' '))}&nav=1`}
                  >
                    Ver no Google Maps
                  </a>
                </div>

                {im.imagens.length > 0 && (im.links.fotos || ativo) && (
                  <div className="border rounded p-3 mb-3">
                    <div className="fw-semibold mb-2">Compartilhar</div>
                    <div className="d-flex flex-wrap gap-2">
                      {ativo && (
                        <Button className="btn-share btn-whatsapp" as="a" target="_blank" rel="noreferrer" href={`https://api.whatsapp.com/send?text=${encodeURIComponent(textoWhats)}`}>
                          WhatsApp
                        </Button>
                      )}
                      {linkFacebook && (
                        <Button className="btn-share btn-fb" as="a" target="_blank" rel="noreferrer" href={linkFacebook}>
                          Facebook
                        </Button>
                      )}
                      {linkPortal && (
                        <>
                          <Button className="btn-share btn-linkshare" as="a" target="_blank" rel="noreferrer" href={linkPortal}>
                            Link
                          </Button>
                          <Button className="btn-share btn-linkshare" onClick={() => copiar(linkPortal)}>
                            Copiar link
                          </Button>
                        </>
                      )}
                      {im.links.fotos && (
                        <Button className="btn-share btn-receberfotos" as="a" target="_blank" rel="noreferrer" href={im.links.fotos}>
                          Receber fotos
                        </Button>
                      )}
                      {im.links.grupos && (
                        <Button className="btn-share btn-grupos" as="a" target="_blank" rel="noreferrer" href={im.links.grupos}>
                          Enviar para grupos
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <details className="border rounded p-3">
                  <summary className="fw-semibold">Proprietário e descrição interna</summary>
                  <div className="mt-2">
                    {im.proprietario ? (
                      <>
                        <div>{im.proprietario.nome}</div>
                        {im.proprietario.email && <div>{im.proprietario.email}</div>}
                        {im.proprietario.telefone && <div>{im.proprietario.telefone}</div>}
                        {im.proprietario.celular && (
                          <div>
                            {im.proprietario.celular}{' '}
                            {celularProp && (
                              <a target="_blank" rel="noreferrer" href={`https://wa.me/55${celularProp}?text=${encodeURIComponent('Olá ')}`}>
                                WhatsApp
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted">Proprietário não informado</div>
                    )}
                    {im.i1_descrinterna && <div className="mt-2 text-secondary" style={{ whiteSpace: 'pre-line' }}>{im.i1_descrinterna}</div>}
                  </div>
                </details>
              </Col>

              <Col md={6}>
                <div className="border rounded p-3">
                  <Row className="text-center mb-2">
                    <Col>
                      <div className="small text-muted">Terreno</div>
                      <strong>{im.i1_totalterreno || '—'}</strong>
                    </Col>
                    <Col>
                      <div className="small text-muted">Dorm.</div>
                      <strong>{im.i1_qtddormitorios ?? '—'}</strong>
                    </Col>
                    <Col>
                      <div className="small text-muted">Garagem</div>
                      <strong>{im.i1_qtdgaragens ?? '—'}</strong>
                    </Col>
                    <Col>
                      <div className="small text-muted">Banh.</div>
                      <strong>{im.i1_qtdbanheiros ?? '—'}</strong>
                    </Col>
                  </Row>
                  <hr />
                  <div className="fw-semibold">Descrição</div>
                  <div className="text-secondary" style={{ whiteSpace: 'pre-line', maxHeight: 335, overflowY: 'auto' }}>
                    {im.i1_descricao || <span className="text-muted">Sem descrição</span>}
                  </div>
                </div>
              </Col>
            </Row>
            {/* registro discreto, no fim: não é para ser visto toda hora */}
            {textoDatasImovel(im) && <div className="text-muted small mt-4 pt-2 border-top">{textoDatasImovel(im)}</div>}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div>
          {im?.pode_excluir && (
            <Button variant="outline-danger" onClick={excluir} disabled={excluindo}>
              {excluindo ? <Spinner animation="border" size="sm" /> : 'Excluir'}
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          {im && usuario.permissoes?.imoveis_criar === true && (
            <Button variant="outline-secondary" onClick={() => setCopiando(true)} title="Criar um imóvel novo igual a este">
              Copiar
            </Button>
          )}
          {im?.pode_editar && (
            <Button as={Link} replace to={`/imoveis/${publicId}`} variant="success" className="botao-salvar">
              Alterar
            </Button>
          )}
          {im && !im.pode_editar && (
            <Button as={Link} replace to={`/imoveis/${publicId}`} variant="outline-secondary">
              Abrir
            </Button>
          )}
          <Button variant="secondary" onClick={aoFechar}>
            Fechar
          </Button>
        </div>
      </Modal.Footer>
      {im && (
        <ModalCopiarImovel
          show={copiando}
          aoFechar={() => setCopiando(false)}
          publicId={publicId}
          codigo={im.i1_codsequencia}
          totalFotos={im.imagens?.length}
          aoCopiado={(novo) => { setCopiando(false); aoFechar(); navegar(`/imoveis/${novo.public_id}`); }}
        />
      )}
    </Modal>
  );
}
